import { ChevronDown, ChevronUp } from "baseui/icon";
import moment from "moment/moment";
import React, { FC, useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import { AvatarPlaceholder } from "../../../../../../components/avatar/AvatarPlaceholder";
import Button from "../../../../../../components/button/Button";
import { LottieLoading } from "../../../../../../components/graphics/LottieLoading";
import { ListPager } from "../../../../../../components/pagination/ListPager";
import {
  getInitials,
  getInitialsFromFullName,
  inactiveStatusId,
} from "../../../../../../constants/constants";
import { updateInvoiceStatusFactory } from "../../../../../../factories/billing/factory-update-invoce-status";
import { TimePeriod } from "../../../../../../hooks/useDetectTimePeriod";
import { useInvoiceFiltersController } from "../../../../../../hooks/useInvoiceFiltersController";
import {
  Invoice,
  SortEnum,
  useGetInvoicesQuery,
  useGetInvoiceStatusesQuery,
  useSetInvoiceLabelMutation,
} from "../../../../../../services/BillingService";
import { getLinkedFirm } from "../../../../../../sessionStorage/sessionStorage";
import { IContactProfile } from "../../../../../../types/types";
import DropStatus from "../../../../../billing/DropStatus";
import { ContactInvoicesFilters } from "../../../../../billing/invoices/ContactInvoicesFilters";
import { invoiceStatusLabel } from "../../../../../billing/ui/invoiceStatus.utils";
import ErrorPopup from "../../../../modals/ErrorPopup";
import { navigateToContact } from "../../../../utils/navigateToContacts";

interface Props {
  customerId: string;
  contact: IContactProfile;
}

const PAGE_SIZE = 20;

const ContactInvoices: FC<Props> = ({ customerId, contact }) => {
  const navigate = useNavigate();
  const isActive = contact?.contactStatusTypeEnumId !== inactiveStatusId;
  const orgId = getLinkedFirm()?.orgId || "";

  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<{ name: SortEnum; order: boolean }>({
    name: SortEnum.InvoiceDate,
    order: false,
  });
  const [activeInvoiceId, setActiveInvoiceId] = useState("");
  const [isOpenError, setIsOpenError] = useState(false);

  const filters = useInvoiceFiltersController({
    defaultPeriod: TimePeriod.All,
  });
  const isAllDateFilter =
    !filters.filter.period || filters.filter.period === TimePeriod.All;

  const [updateInvoice, { isLoading: isLoadingUpdate }] =
    useSetInvoiceLabelMutation();

  const {
    data: invoices,
    isLoading,
    isFetching,
  } = useGetInvoicesQuery(
    {
      pageNumber: page,
      pageSize: PAGE_SIZE,
      orgId: orgId,
      filterByDate: isAllDateFilter ? [] : ["Custom"],
      fromDate: isAllDateFilter
        ? null
        : filters.from
        ? filters.from.toISOString()
        : null,
      toDate: isAllDateFilter
        ? null
        : filters.to
        ? filters.to.toISOString()
        : null,
      sortBy: sort.name,
      sortOrder: sort.order ? "asc" : "desc",
      filterByStatus: filters.filterByStatus.map((item) => item.value),
      filterByClient: [customerId],
      filterByAssignee: filters.filterByUser.map((item) => item.value),
      filterByLabel: filters.filterByLabel.map((item) => item.value),
    },
    { skip: !orgId || !customerId },
  );

  const { data: statuses, isLoading: isLoadingStatuses } =
    useGetInvoiceStatusesQuery(orgId, { skip: !orgId });

  const handleSort = (name: SortEnum) => {
    if (name === sort.name) {
      setSort({ name, order: !sort.order });
    } else {
      setSort({ name, order: true });
    }
  };

  const updateStatus = (statusId: string, invoice: Invoice) => {
    const body = updateInvoiceStatusFactory.createModelFromDto({
      invoice,
      status: statusId || null,
    });

    updateInvoice(body);
  };

  const totalRecords = invoices?.[0]?.totalRecords ?? 0;

  if (isLoading || isLoadingStatuses) {
    return <LottieLoading />;
  }

  const renderSortIcon = (name: SortEnum) => {
    if (sort.name !== name) return null;

    return (
      <div className={"relative left-0 top-[-4px]"}>
        {sort.order ? (
          <div className={"absolute"}>
            <ChevronUp size={26} />
          </div>
        ) : (
          <div className={"absolute"}>
            <ChevronDown size={26} />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className={"mb-4 flex items-end justify-between"}>
        <ContactInvoicesFilters controller={filters} />

        <Button
          label={"+ Add Invoice"}
          size={"custom"}
          extraClasses={"btn-sm px-2"}
          onClick={() => {
            if (!isActive) {
              setIsOpenError(true);
              return;
            }

            const contactName = `${contact.firstName || ""} ${
              contact.lastName || ""
            }`.trim();

            navigate(`/billing/invoices/add?customerId=${customerId}`, {
              state: {
                defaultCustomer: {
                  value: customerId,
                  label: `${contactName}${
                    contact.isSyncedWithQBO ? " (synced)" : ""
                  }`,
                  avatar: getInitials(contact.firstName, contact.lastName),
                  avatarColor: "blue",
                },
              },
            });
          }}
        />
      </div>

      <table className={"w-full"}>
        <thead className={"border-b-[1px] border-gray-300"}>
          <tr>
            <th className={"py-4 pl-4 pr-8 text-gray-500 last:pr-4"}>
              <div
                className={
                  "flex cursor-pointer whitespace-nowrap text-sm normal-case"
                }
                onClick={() => handleSort(SortEnum.Customer)}>
                <div>Contact</div>
                {renderSortIcon(SortEnum.Customer)}
              </div>
            </th>

            <th className={"py-4 pl-4 pr-8 text-gray-500 last:pr-4"}>
              <div
                className={
                  "flex cursor-pointer whitespace-nowrap text-sm normal-case"
                }
                onClick={() => handleSort(SortEnum.InvoiceNo)}>
                <div>Invoice No.</div>
                {renderSortIcon(SortEnum.InvoiceNo)}
              </div>
            </th>

            <th className={"py-4 pl-4 pr-8 text-gray-500 last:pr-4"}>
              <div
                className={
                  "flex cursor-pointer whitespace-nowrap text-sm normal-case"
                }
                onClick={() => handleSort(SortEnum.InvoiceDate)}>
                <div>Invoice date</div>
                {renderSortIcon(SortEnum.InvoiceDate)}
              </div>
            </th>

            <th className={"py-4 pl-4 pr-8 text-gray-500 last:pr-4"}>
              <div
                className={
                  "flex cursor-pointer whitespace-nowrap text-sm normal-case"
                }
                onClick={() => handleSort(SortEnum.DueDate)}>
                <div>Due date</div>
                {renderSortIcon(SortEnum.DueDate)}
              </div>
            </th>

            <th className={"py-4 pl-4 pr-8 text-gray-500 last:pr-4"}>
              <div
                className={
                  "flex cursor-pointer whitespace-nowrap text-sm normal-case"
                }
                onClick={() => handleSort(SortEnum.Total)}>
                <div>Total</div>
                {renderSortIcon(SortEnum.Total)}
              </div>
            </th>

            <th className={"py-4 pl-4 pr-8 text-gray-500 last:pr-4"}>
              <div
                className={
                  "flex cursor-pointer whitespace-nowrap text-sm normal-case"
                }
                onClick={() => handleSort(SortEnum.Balance)}>
                <div>Balance</div>
                {renderSortIcon(SortEnum.Balance)}
              </div>
            </th>

            <th className={"py-4 pl-4 pr-8 text-gray-500 last:pr-4"}>
              <div
                className={
                  "flex cursor-pointer whitespace-nowrap text-sm normal-case"
                }
                onClick={() => handleSort(SortEnum.Status)}>
                <div>Status</div>
                {renderSortIcon(SortEnum.Status)}
              </div>
            </th>

            <th className={"py-4 pl-4 pr-8 text-gray-500 last:pr-4"}>
              <div
                className={
                  "flex cursor-pointer whitespace-nowrap text-sm normal-case"
                }
                onClick={() => handleSort(SortEnum.AssignedUser)}>
                <div>Assignee</div>
                {renderSortIcon(SortEnum.AssignedUser)}
              </div>
            </th>

            <th className={"py-4 pl-4 pr-8 text-gray-500 last:pr-4"}>
              <div
                className={
                  "flex cursor-pointer whitespace-nowrap text-sm normal-case"
                }
                onClick={() => handleSort(SortEnum.InvoiceStatus)}>
                <div>Label</div>
                {renderSortIcon(SortEnum.InvoiceStatus)}
              </div>
            </th>
          </tr>
        </thead>

        <tbody className={"text-sm"}>
          {invoices?.map((invoice) => (
            <tr
              key={invoice?.id}
              className={
                "cursor-pointer border-b-[1px] border-gray-300 last-of-type:border-none hover:bg-gray-100"
              }
              onClick={() =>
                navigate("/billing/invoices/" + invoice.invoiceId)
              }>
              <td className={"bg-transparent px-[16px] py-[8px]"}>
                <div className={"flex items-center"}>
                  <div className={"flex items-center text-[14px]"}>
                    <AvatarPlaceholder
                      size={"extra-small"}
                      label={getInitialsFromFullName(
                        invoice?.customerName || "",
                      )}
                    />
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(
                          navigateToContact({
                            type: "contact",
                            customerId: invoice?.customerId || "",
                          }),
                        );
                      }}
                      className={
                        "link-primary cursor-pointer pl-2 text-sm font-semibold hover:underline"
                      }>
                      {invoice?.customerName}
                    </div>
                  </div>
                </div>
              </td>

              <td
                className={
                  "bg-transparent px-4 py-2 text-sm font-semibold text-gray-800"
                }>
                {invoice?.invoiceNumber}
              </td>

              <td
                className={
                  "bg-transparent px-4 py-2 text-sm font-semibold text-gray-800"
                }>
                {moment(invoice?.invoiceDate).format("MMM DD, YYYY")}
              </td>

              <td
                className={`bg-transparent px-4 py-2 text-sm font-semibold ${
                  invoice?.status === "OverDue"
                    ? "text-red-500"
                    : "text-gray-800"
                }`}>
                {moment(invoice?.dueDate).format("MMM DD, YYYY")}
              </td>

              <td
                className={
                  "bg-transparent px-4 py-2 text-sm font-semibold text-gray-800"
                }>
                $
                {(invoice?.total || 0).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </td>

              <td
                className={
                  "bg-transparent px-4 py-2 text-sm font-semibold text-gray-800"
                }>
                $
                {(invoice?.balance || 0).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </td>

              <td
                className={
                  "bg-transparent px-4 py-2 text-sm font-semibold text-gray-800"
                }>
                <div className={"flex items-center gap-2"}>
                  {invoice?.status === "Paid" && (
                    <FaCheckCircle className={"h-4 w-4 text-green-500"} />
                  )}
                  {invoiceStatusLabel(invoice?.status)}
                </div>
              </td>

              <td className={"relative bg-transparent px-4 py-2"}>
                {invoice?.userId && (
                  <div className={"flex items-center text-sm"}>
                    <AvatarPlaceholder
                      size={"extra-small"}
                      type={"custom"}
                      avatarCustomColor={invoice?.userColor}
                      label={getInitials(
                        invoice?.userFirstName || "",
                        invoice?.userLastName || "",
                      )}
                    />
                    <div
                      className={
                        "whitespace-nowrap pl-2 text-sm font-semibold normal-case text-gray-800"
                      }>
                      {`${invoice?.userFirstName} ${invoice?.userLastName}`}
                    </div>
                  </div>
                )}
              </td>

              <td className={"bg-transparent px-4 py-2"}>
                <DropStatus
                  invoiceStatus={{
                    id: invoice?.labelId,
                    name: invoice?.label,
                    color: invoice?.labelColor,
                  }}
                  paymentStatus={null}
                  statuses={statuses || []}
                  isLoading={
                    (isLoadingUpdate || isFetching) &&
                    invoice?.invoiceId === activeInvoiceId
                  }
                  selectStatus={(status) => {
                    setActiveInvoiceId(invoice?.invoiceId);
                    updateStatus(status?.id, {
                      ...invoice,
                      id: invoice?.invoiceId,
                    });
                  }}
                />
              </td>
            </tr>
          ))}

          <tr className={"sticky bottom-0 left-0 bg-gray-100"}>
            <td className={"px-4 py-2"}></td>
            <td className={"px-4 py-2"}></td>
            <td className={"px-4 py-2"}></td>
            <td className={"px-4 py-2"}></td>
            <td
              className={"px-4 py-2 text-end text-sm font-bold text-gray-800"}>
              Total:
            </td>
            <td className={"px-4 py-2 text-sm font-bold text-gray-800"}>
              $
              {(invoices?.[0]?.sumOfTotal || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </td>
            <td className={"px-4 py-2 text-sm font-bold text-gray-800"}>
              $
              {(invoices?.[0]?.sumOfBalance || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </td>
            <td className={"px-4 py-2"}></td>
            <td className={"px-4 py-2"}></td>
          </tr>
        </tbody>
      </table>

      {!!invoices?.length && (
        <ListPager
          page={page}
          pageSize={PAGE_SIZE}
          total={totalRecords}
          disabled={isFetching}
          onChange={setPage}
          className={"mt-2 px-0"}
        />
      )}

      <ErrorPopup
        closePopup={() => setIsOpenError(false)}
        isOpen={isOpenError}
      />
    </>
  );
};

export default ContactInvoices;
